// src/components/Contact.tsx
import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  SiFacebook,
  SiGithub,
  SiInstagram,
  SiX,
  SiWhatsapp,
} from '@icons-pack/react-simple-icons';
import ContactForm from '@/components/ContactForm';
import AnimatedDotBackground from '@/components/ui/AnimatedDotBackground';

// WhatsApp Business number (Cloud API) - digits only, used to build the wa.me link
const WHATSAPP_NUMBER = '15553162280';
const WHATSAPP_LINK = `https://wa.me/${WHATSAPP_NUMBER}`;

const Contact = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <section id="contact" className="py-48 bg-white relative">
      {/* Animated dot background */}
      <AnimatedDotBackground
        intensity="strong"
        speed="fast"
        fadeCenter={true}
        enhancedCorners={true}
      />

      <div className="container mx-auto px-4 relative z-10" ref={ref}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 max-w-6xl mx-auto text-center md:text-left">
          {/* Left Column - Heading and Contact Info */}
          <motion.div
            className="space-y-10"
            initial="hidden"
            animate={isInView ? 'visible' : 'hidden'}
            variants={{
              hidden: {},
              visible: {
                transition: {
                  staggerChildren: 0.2,
                },
              },
            }}>
            <motion.h2
              variants={itemVariants}
              className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight text-primary">
              Let's Talk
            </motion.h2>

            {/* Email */}
            <motion.div variants={itemVariants}>
              <h3 className="text-base font-semibold text-gray-400">
                Email Us
              </h3>
              <a
                href="mailto:hello@hoptira.com"
                className="text-2xl font-semibold text-secondary hover:opacity-80 transition-opacity">
                hello@hoptira.com
              </a>
            </motion.div>

            {/* WhatsApp */}
            <motion.div variants={itemVariants}>
              <h3 className="text-base font-semibold text-gray-400">
                WhatsApp Us
              </h3>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-xl font-medium text-gray-500 hover:opacity-80 transition-opacity">
                <SiWhatsapp size={20} />
                WhatsApp us
              </a>
            </motion.div>

            {/* Address */}
            <motion.div variants={itemVariants}>
              <h3 className="text-base font-semibold text-gray-400">
                Write Us
              </h3>
              <p className="text-xl font-medium text-gray-500">
                Hoptira Labs LLC
                <br />
                30 N Gould St Ste R,
                <br />
                Sheridan, WY 82801
              </p>
            </motion.div>

            {/* Social media icons */}
            <motion.div variants={itemVariants}>
              <h3 className="text-base font-semibold text-gray-400">
                Follow Us
              </h3>
              <div className="flex space-x-6 md:justify-start justify-center mt-2">
                <a
                  href="https://facebook.com/hoptira"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-blue-600 transition-colors">
                  <SiFacebook size={24} />
                </a>
                <a
                  href="https://instagram.com/hoptiralabs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-pink-600 transition-colors">
                  <SiInstagram size={24} />
                </a>
                <a
                  href="https://twitter.com/hoptira"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-black transition-colors">
                  <SiX size={24} />
                </a>
                <a
                  href="https://github.com/hoptira"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-500 hover:text-black transition-colors">
                  <SiGithub size={24} />
                </a>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Column - Contact Form */}
          <motion.div
            className="bg-white mt-12 md:mt-28"
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
            transition={{ duration: 0.6, delay: 0.2 }}>
            <ContactForm />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
